"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const three_1 = require("three");
const consts_1 = require("../../consts");
// eslint-disable-next-line import/no-cycle
const utils_1 = require("../../utils");
// eslint-disable-next-line import/no-cycle
const BaseObject3D_3d_1 = __importDefault(require("../BaseObject3D.3d"));
const SlotCoverUtils_1 = require("./SlotCoverUtils");
const tempVector = new three_1.Vector3();
// eslint-disable-next-line no-shadow
var SlotSnappingObstructionType;
(function (SlotSnappingObstructionType) {
    SlotSnappingObstructionType["aboveSlot"] = "aboveSlot";
    SlotSnappingObstructionType["inSlotThickness"] = "inSlotThickness";
    SlotSnappingObstructionType["inSlotDepth"] = "inSlotDepth";
})(SlotSnappingObstructionType || (SlotSnappingObstructionType = {}));
class SlotCoverSnappingBase {
    constructor(viewer, cover) {
        this.treshold = 0;
        this.viewer = viewer;
        this.scene = this.viewer.getScene();
        this.cover = cover;
        this.defaultSlotSnappingObstructionDetectionStartOffsets = {
            [SlotSnappingObstructionType.aboveSlot]: 0.01,
            [SlotSnappingObstructionType.inSlotThickness]: 0,
            [SlotSnappingObstructionType.inSlotDepth]: 0,
        };
        this.obstructionDetectionMap = {
            [SlotSnappingObstructionType.aboveSlot]: new Map(),
            [SlotSnappingObstructionType.inSlotThickness]: new Map(),
            [SlotSnappingObstructionType.inSlotDepth]: new Map(),
        };
    }
    orientCoverOnSlot(slot) {
        const { cover } = this;
        const coverPosition = cover.position;
        const xDirection = slot.direction;
        const yDirection = slot.normal;
        const zDirection = new three_1.Vector3().copy(xDirection).cross(yDirection);
        const extrusionMatrix = new three_1.Matrix4().makeBasis(xDirection, yDirection, zDirection);
        const coverMatrix = (0, SlotCoverUtils_1.getBasisMatrix)(cover);
        cover.applyMatrix4(extrusionMatrix.multiply(coverMatrix.invert()));
        cover.setPosition(coverPosition);
    }
    snapToSlot(slot, extrusion, intersectionPoint, skipSameSlotCheck = false) {
        if ((extrusion === null || extrusion === void 0 ? void 0 : extrusion.type) !== 'Extrusion') {
            return false;
        }
        if (!this.cover.getRectangles()) {
            console.log(`The component '${this.cover.partDetails.itemNumber}' is missing required metadata`);
            return false;
        }
        if (!skipSameSlotCheck && this.isSnappedToSameSlot(slot.surfacePoint, extrusion.uuid)) {
            return true;
        }
        const { cover } = this;
        this.slotDirection = slot.direction;
        const slotBottomSurfPoint = slot.surfacePoint.clone().add(this.slotDirection
            .clone()
            .negate()
            .multiplyScalar(extrusion.getLength() / 2));
        this.coverPosOnSlot = intersectionPoint
            .clone()
            .projectOnVector(this.slotDirection)
            .sub(slotBottomSurfPoint.clone().projectOnVector(this.slotDirection))
            .add(slotBottomSurfPoint);
        if (!skipSameSlotCheck && this.isPointAlreadyCovered(this.coverPosOnSlot, slot.surfacePoint, extrusion.uuid)) {
            return false;
        }
        this.restoreDefaultProps();
        this.viewer.getDesignProMain().highlightingTool.clearConnectionHighlight();
        const coverMove = new three_1.Vector3()
            .subVectors(this.coverPosOnSlot, (0, SlotCoverUtils_1.getSnappingPoint)(cover))
            .add(slot.normal.clone().setLength(0.5));
        cover.translate(coverMove);
        this.orientCoverOnSlot(slot);
        // #region Place cover(s) on the slot
        /*
          1. Send rays to 'bottom' and 'top' of extrusion and find the values of 'distanceToTop' and 'distanceToBottom' variables;
          2. Using 'distanceToTop' and 'distanceToBottom' variables get free spa of slot between intersections or extrusion edges ('toCoverLength' variable);
          3. If free space longer than max cover's length - get count of covers('coverCount' variable);
          4. Try to place each cover on the slot, if this part of slot is already covered - place cover only on free space.
        */
        const { distanceToTop, distanceToBottom } = this.getDistancesToTopAndBottomForSnapping(slot, extrusion, slotBottomSurfPoint);
        const bottomPoint = this.coverPosOnSlot.clone().sub(this.slotDirection.clone().multiplyScalar(distanceToBottom));
        let toCoverLength = utils_1.Utils.roundTo3(distanceToBottom + distanceToTop < extrusion.getLength()
            ? distanceToBottom + distanceToTop - 2 * this.treshold
            : extrusion.getLength() - 2 * this.treshold);
        let coverCount = 0;
        const maxComponentLength = this.getMaxComponentLength(toCoverLength);
        coverCount = Math.ceil(toCoverLength / maxComponentLength);
        cover.userData.tempCovers = cover.userData.tempCovers || new Map();
        let placed = false;
        for (let i = 0; i < coverCount; i += 1) {
            let currLength = toCoverLength <= maxComponentLength ? toCoverLength : maxComponentLength;
            const isFirstCover = i === 0;
            const isLastCover = i === coverCount - 1;
            if (isLastCover) {
                currLength -= this.treshold;
            }
            const currCover = isFirstCover ? cover : cover.clone();
            currCover.userData = currCover.userData || {};
            currCover.userData.shouldNotBeCentered = true;
            if (!isFirstCover) {
                const points = (0, SlotCoverUtils_1.getRectanglePoints)(currCover);
                (0, SlotCoverUtils_1.copyLeftAndRightPoints)(points, points);
            }
            const startPos = isFirstCover
                ? bottomPoint.clone().add(this.slotDirection.clone().multiplyScalar(this.treshold))
                : bottomPoint.clone().add(this.slotDirection.clone().multiplyScalar(maxComponentLength * i));
            const [startFreePos, freeSpaceLength] = this.getFreeSpaceLengthOnSlot(slot.surfacePoint, extrusion.uuid, startPos, currLength);
            if (freeSpaceLength <= 0) {
                placed = placed || false;
                // eslint-disable-next-line no-continue
                continue;
            }
            (0, SlotCoverUtils_1.placeCoverOnSlot)(currCover, startFreePos, currLength <= freeSpaceLength ? currLength : freeSpaceLength);
            placed = true;
            if (!isFirstCover) {
                this.scene.add(currCover);
                cover.userData.tempCovers.set(currCover.uuid, currCover);
            }
            toCoverLength -= currLength;
            currCover.snapped = true;
            currCover.userData.extrusionId = extrusion.uuid;
            currCover.userData.surfacePoint = slot.surfacePoint;
            currCover.isRotatedForSnapping = true;
        }
        // #endregion
        return placed;
    }
    /**
     * This method determines the distance to top and distance to bottom for snapping slot cover using below algorithm:
     * 1. Set the offsets for obstruction detection ray origin from precalculated cover position on slot. The offsets should push the origin in or out of the slot i.e. parallel to the slot normal.
     * 2. Fill the snapping obstruction detection map in order to cut the slot cover to not collide with interfering items.
     * 3. Get distance to top from each obstruction detection starts position and take minimum distance of them as the distance to top.
     * 4. Get distance to bottom from each obstruction detection starts position and take minimum distance of them as the distance to bottom.
     * @param slot                  Slot to which the cover is to be snapped.
     * @param extrusion             Extrusion that has the given slot.
     * @param slotBottomSurfPoint   Precalculated point on the bottom surface of the slot.
     * @returns Object containing distance to top and distance to bottom for cutting the slot cover.
     */
    getDistancesToTopAndBottomForSnapping(slot, extrusion, slotBottomSurfPoint) {
        let distanceToTop = extrusion.getLength();
        let distanceToBottom = distanceToTop;
        this.setObstructionDetectionOffsets(slot);
        this.clearObstructionDetectionMap();
        // Pass 2 as remainingLevels to consider the fasteners and slot covers connected to the extrusions that are connected to the extrusion to which snapping is to be done.
        this.fillSlotSnappingObstructionDetectionMap(extrusion, [extrusion.uuid, this.cover.uuid], true, 2);
        Object.keys(SlotSnappingObstructionType).forEach((key) => {
            const { distanceToTop: distanceToTopForCurrentObstructionType, distanceToBottom: distanceToBottomCurrentObstructionType, } = this.getDistanceToTopAndBottomForObstructionType(slot, extrusion, slotBottomSurfPoint, key);
            if (distanceToBottomCurrentObstructionType < distanceToBottom) {
                distanceToBottom = distanceToBottomCurrentObstructionType;
            }
            if (distanceToTopForCurrentObstructionType < distanceToTop) {
                distanceToTop = distanceToTopForCurrentObstructionType;
            }
        });
        return {
            distanceToTop,
            distanceToBottom,
        };
    }
    /**
     * This method determines distance to top and distance to bottom from a start position for given obstruction type using below algorithm:
     * 1. Initialize the distances from precalculated cover position to the ends of extrusion as distance to top and bottom.
     * 2. Detect the obstructions by casting ray in the direction of slot from start position for given obstruction type and take the distance to the first obstruction as the distance to top if found.
     * 3. Detect the obstructions by casting ray in the negative direction of slot from start position for given obstruction type and take the distance to the first obstruction as the distance to bottom if found.
     * @param slot
     * @param extrusion
     * @param slotBottomSurfPoint
     * @param obstructionType
     * @returns
     */
    getDistanceToTopAndBottomForObstructionType(slot, extrusion, slotBottomSurfPoint, obstructionType) {
        const obstructionDetectionStartOffset = this.obstructionDetectionStartOffsets[obstructionType];
        const possibleObstructingItems = Array.from(this.obstructionDetectionMap[obstructionType].values());
        let distanceToTop = slotBottomSurfPoint
            .clone()
            .projectOnVector(this.slotDirection)
            .add(this.slotDirection.clone().multiplyScalar(extrusion.getLength()))
            .sub(this.coverPosOnSlot.clone().projectOnVector(this.slotDirection))
            .length();
        let intersectBottom = slotBottomSurfPoint;
        if ((possibleObstructingItems === null || possibleObstructingItems === void 0 ? void 0 : possibleObstructingItems.length) && typeof obstructionDetectionStartOffset === 'number') {
            let obstructingItems = [];
            const offsetDirection = slot.normal.clone();
            const obstructionDetectionRayOffsetPos = offsetDirection.setLength(obstructionDetectionStartOffset);
            const obstructionDetectionRayOrigin = this.coverPosOnSlot.clone().add(obstructionDetectionRayOffsetPos);
            const obstructionDetectionRaycaster = new three_1.Raycaster();
            obstructionDetectionRaycaster.set(obstructionDetectionRayOrigin, this.slotDirection.clone().negate());
            obstructingItems = (0, SlotCoverUtils_1.getIntersectedObjects)(obstructionDetectionRaycaster, possibleObstructingItems);
            if (obstructingItems.length > 0) {
                intersectBottom = obstructingItems[0].point.clone().sub(obstructionDetectionRayOffsetPos);
            }
            obstructionDetectionRaycaster.set(obstructionDetectionRayOrigin, this.slotDirection);
            obstructingItems = (0, SlotCoverUtils_1.getIntersectedObjects)(obstructionDetectionRaycaster, possibleObstructingItems);
            if (obstructingItems.length > 0) {
                distanceToTop = obstructingItems[0].distance;
            }
        }
        const distanceToIntersectBottom = intersectBottom.clone().sub(this.coverPosOnSlot.clone()).length();
        const distanceToEndExtrusionBottom = slotBottomSurfPoint.clone().sub(this.coverPosOnSlot.clone()).length();
        const distanceToBottom = distanceToIntersectBottom > distanceToEndExtrusionBottom
            ? distanceToEndExtrusionBottom
            : distanceToIntersectBottom;
        return {
            distanceToTop,
            distanceToBottom,
        };
    }
    /**
     * This method sets the offsets according to given slot.
     * @param slot  Slot to which the cover is to be snapped.
     */
    setObstructionDetectionOffsets(slot) {
        this.obstructionDetectionStartOffsets = Object.assign(Object.assign({}, this.defaultSlotSnappingObstructionDetectionStartOffsets), { inSlotThickness: -slot.thickness / 2, inSlotDepth: -(slot.thickness + slot.depth / 2) });
    }
    clearObstructionDetectionMap() {
        Object.values(this.obstructionDetectionMap).forEach((map) => map.clear());
    }
    /**
     * This method fills the slot snapping obstruction detection map recursively by considering the related connection objects of the given host object.
     * @param connectionHostObject  Object to which the related connections are to be taken. Because the related connections can be the possible obstructions.
     * @param excludeUUIDs          UUIDs of the objects to be excluded (e.g. UUID of the extrusion to which cover is to be snapped, UUID of the cover that is being snapped, etc.)
     * @param recursive             Flat stating whether the obstruction detection map be filled recursively with the objects connected to the connections of given connection host.
     * @param remainingLevels       Remaining levels of recursive connection detection. Considered only if `recursive` flag is `true`.
     * @returns
     */
    fillSlotSnappingObstructionDetectionMap(connectionHostObject, excludeUUIDs, recursive = true, remainingLevels = 1) {
        if (recursive && remainingLevels <= 0) {
            return;
        }
        const connectionObjectsRelatedToConnectionHost = this.viewer
            .getDesignProMain()
            .connectionManager.getRelatedConnectionObjects(connectionHostObject);
        connectionObjectsRelatedToConnectionHost.forEach((connectionObject) => {
            const [mainObject, connectedObject] = [connectionObject.getMainObject(), connectionObject.getConnectedObject()];
            if (mainObject && mainObject.uuid !== connectionHostObject.uuid && !excludeUUIDs.includes(mainObject.uuid)) {
                this.tryToSetItemInObstructionDetectionMap(mainObject, SlotSnappingObstructionType.aboveSlot);
                if (recursive) {
                    this.fillSlotSnappingObstructionDetectionMap(mainObject, excludeUUIDs, recursive, remainingLevels - 1);
                }
            }
            else if (connectedObject &&
                connectedObject.uuid !== connectionHostObject.uuid &&
                !excludeUUIDs.includes(connectedObject.uuid)) {
                this.tryToSetItemInObstructionDetectionMap(connectedObject, SlotSnappingObstructionType.aboveSlot);
                if (recursive) {
                    this.fillSlotSnappingObstructionDetectionMap(connectedObject, excludeUUIDs, recursive, remainingLevels - 1);
                }
            }
            // Fastener should be considered as an obstruction of type inSlotThickness.
            connectionObject.getFasteners().forEach((fastener) => {
                this.tryToSetItemInObstructionDetectionMap(fastener, SlotSnappingObstructionType.inSlotThickness);
            });
        });
    }
    /**
     * This method tries to set the given item in the obstruction detection map for the given obstruction type.
     * @param item              Possible obstructing item.
     * @param obstructionType   Obstruction type of possible obstructing item.
     */
    tryToSetItemInObstructionDetectionMap(item, obstructionType) {
        if (!this.obstructionDetectionMap[obstructionType].has(item.uuid)) {
            this.obstructionDetectionMap[obstructionType].set(item.uuid, item.outerBox);
        }
    }
    getMaxComponentLength(toCoverLength) {
        return this.cover.partDetails.maxStockLength;
    }
    isSnappedToSameSlot(surfacePoint, extrusionId) {
        return (this.cover.userData.extrusionId === extrusionId &&
            utils_1.Utils.areVectorsEqual(surfacePoint, this.cover.userData.surfacePoint) &&
            this.cover.snapped);
    }
    getTheSameSlotCovers(extrusionId, surfacePoint) {
        return this.scene.children.filter((c) => {
            if (!(c instanceof BaseObject3D_3d_1.default)) {
                return false;
            }
            return (c.uuid !== this.cover.uuid &&
                utils_1.Utils.isCoverComponent(c) &&
                c.userData.extrusionId === extrusionId &&
                utils_1.Utils.areVectorsEqual(surfacePoint, c.userData.surfacePoint));
        });
    }
    getFreeSpaceLengthOnSlot(surfacePoint, extrusionId, startPosOnSlot, coverLength) {
        const { slotDirection } = this;
        let newCoverStartPos = startPosOnSlot.clone().projectOnVector(slotDirection);
        let newCoverEndPos = newCoverStartPos.clone().add(tempVector.copy(slotDirection).setLength(coverLength));
        const theSameSlotCovers = this.getTheSameSlotCovers(extrusionId, surfacePoint);
        let retStartPos = startPosOnSlot;
        for (let i = 0; i < theSameSlotCovers.length; i += 1) {
            const c = theSameSlotCovers[i];
            const existCoverLength = c.getLength();
            const existCoverStartPos = (0, SlotCoverUtils_1.getSnappingPoint)(c)
                .clone()
                .projectOnVector(slotDirection);
            const existCoverEndPos = existCoverStartPos.clone().add(slotDirection.clone().setLength(existCoverLength));
            if (utils_1.Utils.areVectorsEqual(newCoverStartPos, existCoverStartPos) &&
                utils_1.Utils.areVectorsEqual(newCoverEndPos, existCoverEndPos)) {
                return [new three_1.Vector3(), 0];
            }
            if (utils_1.Utils.isVectorBetweenVectors(newCoverStartPos, existCoverStartPos, existCoverEndPos) &&
                !utils_1.Utils.areVectorsEqual(newCoverStartPos, existCoverEndPos)) {
                newCoverStartPos = existCoverEndPos;
                retStartPos = (0, SlotCoverUtils_1.getSnappingPoint)(c).add(slotDirection.clone().setLength(existCoverLength));
            }
            if (utils_1.Utils.isVectorBetweenVectors(newCoverEndPos, existCoverStartPos, existCoverEndPos) &&
                !utils_1.Utils.areVectorsEqual(newCoverEndPos, existCoverStartPos)) {
                newCoverEndPos = existCoverStartPos;
            }
        }
        const res = newCoverEndPos.sub(newCoverStartPos);
        return [retStartPos, res.angleTo(slotDirection) === Math.PI ? 0 : res.length()];
    }
    isPointAlreadyCovered(point, surfacePoint, extrusionId) {
        const checkPoint = point.clone().projectOnVector(this.slotDirection);
        const theSameSlotCovers = this.getTheSameSlotCovers(extrusionId, surfacePoint);
        const { slotDirection } = this;
        for (let i = 0; i < theSameSlotCovers.length; i += 1) {
            const c = theSameSlotCovers[i];
            const existCoverLength = c.getLength();
            const existCoverStartPos = (0, SlotCoverUtils_1.getSnappingPoint)(c).clone().projectOnVector(this.slotDirection);
            const existCoverEndPos = existCoverStartPos.clone().add(slotDirection.clone().setLength(existCoverLength));
            if (utils_1.Utils.areVectorsEqual(checkPoint, existCoverStartPos) ||
                utils_1.Utils.areVectorsEqual(checkPoint, existCoverEndPos) ||
                utils_1.Utils.isVectorBetweenVectors(checkPoint, existCoverStartPos, existCoverEndPos)) {
                return true;
            }
        }
        return false;
    }
    getExtrusions() {
        return this.scene.children.filter((e) => {
            return e.type === 'Extrusion';
        });
    }
    restoreDefaultProps() {
        var _a, _b;
        const object = this.cover;
        object.setRotation(utils_1.Utils.identityQuaternion);
        object.setSizeZ(1);
        delete object.userData.extrusionId;
        delete object.userData.surfacePoint;
        this.clearTempCovers();
        delete object.userData.tempCovers;
        object.snapped = false;
        const points = (0, SlotCoverUtils_1.getRectanglePoints)(object);
        let minPoint = (0, SlotCoverUtils_1.getMinPoint)(points, (_a = object.getAlignmentAxis()) !== null && _a !== void 0 ? _a : consts_1.consts.DP_UP_DIR);
        minPoint.bottomPos = 'L';
        const leftPoints = points.filter((p) => utils_1.Utils.areVectorsNotEqual(p, minPoint));
        minPoint = (0, SlotCoverUtils_1.getMinPoint)(leftPoints, (_b = object.getAlignmentAxis()) !== null && _b !== void 0 ? _b : consts_1.consts.DP_UP_DIR);
        minPoint.bottomPos = 'R';
    }
    clearTempCovers() {
        const { tempCovers } = this.cover.userData;
        tempCovers === null || tempCovers === void 0 ? void 0 : tempCovers.forEach((t) => this.scene.remove(t));
        tempCovers === null || tempCovers === void 0 ? void 0 : tempCovers.clear();
    }
    getTempObjects() {
        return this.cover.userData.tempCovers;
    }
    // eslint-disable-next-line class-methods-use-this
    getRaycastPos() {
        return [0.01];
    }
    getSlotPoints() {
        if (this.coverPosOnSlot) {
            return [utils_1.Utils.roundVectorTo3(this.coverPosOnSlot)];
        }
        return (0, SlotCoverUtils_1.getRectanglePoints)(this.cover);
    }
}
exports.default = SlotCoverSnappingBase;
